import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { useDebounce } from "@uidotdev/usehooks";
import {
  Archive,
  ArrowRight,
  ChevronDown,
  MapPin,
  MessageSquareDot,
  PackagePlus,
  Search,
  Star,
} from "lucide-react";
import { useCallback, useState } from "react";

import type { ConversationSummaryDto, DeliveryStatus } from "@mandoob/shared";

import {
  getConversations,
  getGetCountsQueryKey,
  useGetCounts,
  useMarkRead,
  useSetStarred,
} from "@/api/inbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/language";
import { formatPhoneNumberForDisplay } from "@/lib/phone-utils";
import { cn } from "@/lib/utils";

import { ConversationAvatar } from "./conversation-avatar";
import {
  DELIVERY_STATUS_KEYS,
  DeliveryStatusBadge,
} from "./delivery-status-badge";
import { DeliveryTicks } from "./delivery-ticks";
import { formatConversationTime, localizePreview, Num } from "./lib";
import { AppContextMenu, OptionsMenu, type MenuOption } from "./options-menu";
import { SessionPill } from "./session-pill";

const PAGE_SIZE = 50;

/** null = all statuses; "Undelivered" = New + Ongoing (the default view). */
type StatusFilter = DeliveryStatus | "Undelivered" | null;
// Only the three that matter to a driver: in delivery (default), delivered,
// cancelled. All/New/Ongoing stay reachable through the API but have no chip.
const STATUS_FILTERS: StatusFilter[] = [
  "Undelivered",
  "Delivered",
  "Cancelled",
];

function ConversationRow({
  conversation,
}: {
  conversation: ConversationSummaryDto;
}) {
  const { t, language } = useLanguage();
  const queryClient = useQueryClient();
  const unread = conversation.unreadCount > 0;
  // Clients created from a bare phone have no name of their own.
  const client =
    conversation.name.trim() ||
    formatPhoneNumberForDisplay(conversation.phoneNumber);
  // The tracking number leads; without one the client takes the headline and
  // the bottom line would only repeat it.
  const tracking = conversation.trackingNumber?.trim();

  const invalidateList = () => {
    void queryClient.invalidateQueries({
      queryKey: ["/api/inbox/conversations"],
    });
    void queryClient.invalidateQueries({ queryKey: getGetCountsQueryKey() });
  };
  const setStarred = useSetStarred({
    mutation: { onSuccess: invalidateList },
  });
  const markRead = useMarkRead({ mutation: { onSuccess: invalidateList } });

  const menuOptions: MenuOption[] = [
    {
      type: "item",
      label: t(
        conversation.isStarred ? "Mandoob:Chats.Unstar" : "Mandoob:Chats.Star",
      ),
      icon: Star,
      iconClassName: conversation.isStarred ? "tw:fill-foreground" : undefined,
      onClick: () =>
        setStarred.mutate({
          conversationId: conversation.id,
          isStarred: !conversation.isStarred,
        }),
    },
    ...(unread
      ? [
          {
            type: "item" as const,
            label: t("Mandoob:Chats.MarkRead"),
            icon: MessageSquareDot,
            onClick: () => markRead.mutate({ conversationId: conversation.id }),
          },
        ]
      : []),
  ];

  const row = (
    <Link
      to="/chats/$conversationId"
      params={{ conversationId: conversation.id }}
      className="tw:group/row tw:flex tw:items-center tw:gap-3 tw:border-b tw:border-border/60 tw:px-4 tw:py-3 tw:no-underline tw:transition-colors tw:hover:bg-muted/50 tw:active:bg-muted"
      // Highlights the open conversation in the desktop aside.
      activeProps={{ className: "tw:bg-muted/70" }}
    >
      <span className="tw:flex tw:shrink-0 tw:flex-col tw:items-center tw:gap-1">
        <ConversationAvatar
          name={client}
          trackingNumber={conversation.trackingNumber}
          phone={conversation.phoneNumber}
        />
        <DeliveryStatusBadge status={conversation.deliveryStatus} />
        {/* Finished delivery whose chat a client message reopened. */}
        {conversation.reopened ? (
          <span className="tw:inline-flex tw:shrink-0 tw:items-center tw:rounded-full tw:border tw:border-current tw:bg-warning-soft tw:px-2 tw:py-0.5 tw:text-[10px] tw:font-medium tw:leading-[1.6] tw:text-warning">
            {t("Mandoob:Chats.Reopened")}
          </span>
        ) : null}
      </span>
      <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-0.5">
        <div className="tw:flex tw:items-center tw:gap-2">
          <span
            className={cn(
              "tw:min-w-0 tw:truncate tw:text-base tw:text-foreground",
              unread ? "tw:font-semibold" : "tw:font-medium",
            )}
          >
            <bdi>{tracking ?? client}</bdi>
          </span>
          <span className="tw:ms-auto tw:flex tw:shrink-0 tw:items-center tw:gap-1 tw:text-xs tw:text-muted-foreground">
            {conversation.clientLocation ? (
              <MapPin
                className="tw:size-3 tw:text-brand"
                aria-label={t("Mandoob:Delivery.ClientLocation")}
              />
            ) : null}
            {conversation.isStarred ? (
              <Star
                className="tw:size-3 tw:fill-muted-foreground tw:text-muted-foreground"
                aria-label={t("Mandoob:Chats.Star")}
              />
            ) : null}
            {formatConversationTime(conversation.lastMessageAt, t, language)}
          </span>
        </div>
        <div className="tw:flex tw:items-center tw:gap-1.5">
          {conversation.lastMessageDirection === "Outbound" && (
            <DeliveryTicks delivery={conversation.lastMessageDelivery} />
          )}
          <span
            className={cn(
              "tw:min-w-0 tw:truncate tw:text-sm",
              unread
                ? "tw:font-medium tw:text-foreground"
                : "tw:text-muted-foreground",
            )}
          >
            {localizePreview(conversation.preview, t)}
          </span>
          {unread && (
            <span className="tw:ms-auto tw:flex tw:h-5 tw:min-w-5 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-brand tw:px-1.5 tw:text-xs tw:font-semibold tw:text-brand-foreground">
              <Num>{conversation.unreadCount}</Num>
            </span>
          )}
        </div>
        <div className="tw:mt-0.5 tw:flex tw:items-center tw:gap-2">
          {tracking && (
            <span className="tw:min-w-0 tw:truncate tw:text-[13px] tw:text-muted-foreground">
              <bdi>{client}</bdi>
            </span>
          )}
          <span className="tw:ms-auto tw:flex tw:shrink-0 tw:items-center tw:gap-1">
            <SessionPill
              compact
              windowOpen={!!conversation.windowExpiresAt}
              windowExpiresAt={conversation.windowExpiresAt}
            />
            {/* Hover (fine pointers) / always-on (coarse pointers) menu at the
                row's bottom end — the right-click AppContextMenu is mouse-only,
                so on touch this chevron is the only way in. Click must not
                follow the Link. While hidden the button collapses to ZERO width
                (the negative margin cancels the flex gap) so the session pill
                sits flush at the end; revealing expands it and pushes the pill
                inward, snapping back when the hover ends (reference behavior).
                `has-` variants keep it expanded while its menu is open. */}
            <span
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              className="tw:-ms-1 tw:flex tw:transition-[margin] tw:group-hover/row:ms-0 tw:pointer-coarse:ms-0 tw:has-focus-visible:ms-0 tw:has-[[data-state=open]]:ms-0"
            >
              <OptionsMenu options={menuOptions} align="end">
                <button
                  type="button"
                  aria-label={t("Mandoob:Chats.ConversationMenu")}
                  className="tw:flex tw:h-5 tw:w-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-full tw:text-muted-foreground tw:opacity-0 tw:transition-all tw:group-hover/row:w-5 tw:group-hover/row:opacity-100 tw:pointer-coarse:w-5 tw:pointer-coarse:opacity-100 tw:focus-visible:w-5 tw:focus-visible:opacity-100 tw:data-[state=open]:w-5 tw:data-[state=open]:opacity-100"
                >
                  <ChevronDown className="tw:size-4" strokeWidth={2} />
                </button>
              </OptionsMenu>
            </span>
          </span>
        </div>
      </div>
    </Link>
  );

  return <AppContextMenu options={menuOptions}>{row}</AppContextMenu>;
}

/**
 * Desktop-only pane rendered at /chats beside the persistent conversations
 * aside (WhatsApp-web style) — the list itself lives in the AppShell aside.
 */
export function NoConversationPane() {
  const { t, dir } = useLanguage();
  return (
    <div
      dir={dir}
      className="tw:hidden tw:h-full tw:flex-col tw:items-center tw:justify-center tw:gap-3 tw:px-8 tw:text-center tw:md:flex"
    >
      <img src="/logo.svg" alt="" className="tw:size-16" />
      <p className="tw:m-0 tw:text-lg tw:font-semibold">
        {t("Mandoob:AppName")}
      </p>
      <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
        {t("Mandoob:Chats.SelectPrompt")}
      </p>
    </div>
  );
}

function RowSkeleton() {
  return (
    <div className="tw:flex tw:items-center tw:gap-3 tw:px-4 tw:py-3">
      <Skeleton className="tw:size-11 tw:rounded-full" />
      <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-2">
        <Skeleton className="tw:h-4 tw:w-1/3" />
        <Skeleton className="tw:h-3 tw:w-2/3" />
      </div>
    </div>
  );
}

export function ChatsPage() {
  const { t, dir } = useLanguage();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const filter = debouncedSearch.trim() || undefined;
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("Undelivered");
  // Archive view (chats closed for 24h+): its own page at /chats?archived=true
  // — a distinct header with a back arrow, and no status chips.
  const search_ = useSearch({ strict: false }) as { archived?: true };
  const archived = search_.archived === true;
  const deliveryStatus = archived ? undefined : (statusFilter ?? undefined);

  // Unread-conversation count per chip — a reply landing in a delivered or
  // cancelled chat stays visible from any view (realtime invalidations keep
  // the counts fresh).
  const { data: counts } = useGetCounts();
  const chipUnread: Partial<Record<Exclude<StatusFilter, null>, number>> = {
    Undelivered: counts?.unreadUndelivered ?? 0,
    Delivered: counts?.unreadDelivered ?? 0,
    Cancelled: counts?.unreadCancelled ?? 0,
  };
  // The default "undelivered" view is not a search: an empty list there gets
  // the first-run empty state, not "no results".
  const filtering =
    !!filter || archived || (!!statusFilter && statusFilter !== "Undelivered");

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: [
        "/api/inbox/conversations",
        "infinite",
        { Filter: filter, DeliveryStatus: deliveryStatus, Archived: archived },
      ],
      queryFn: ({ pageParam, signal }) =>
        getConversations(
          {
            SkipCount: pageParam,
            MaxResultCount: PAGE_SIZE,
            Filter: filter,
            DeliveryStatus: deliveryStatus,
            // Only ever sent as true — the API coerces any present value.
            Archived: archived || undefined,
          },
          undefined,
          signal,
        ),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce(
          (sum, page) => sum + page.items.length,
          0,
        );
        return loaded < lastPage.totalCount && lastPage.items.length > 0
          ? loaded
          : undefined;
      },
    });

  const conversations = data?.pages.flatMap((page) => page.items) ?? [];

  const onScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const el = event.currentTarget;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 300;
      if (nearBottom && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  return (
    <div dir={dir} className="tw:relative tw:flex tw:h-full tw:flex-col">
      <header className="tw:shrink-0 tw:border-b tw:border-border tw:px-4 tw:pb-3 tw:pt-[calc(12px+env(safe-area-inset-top))] tw:md:pt-5">
        {archived ? (
          // The archive is its own page: back arrow to the active
          // conversations, archive identity in the title.
          <div className="tw:mb-3 tw:flex tw:items-center tw:gap-1.5">
            <Link
              to="/chats"
              aria-label={t("Mandoob:Back")}
              className="tw:-ms-2 tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-muted-foreground tw:transition-colors tw:hover:bg-muted"
            >
              <ArrowRight className="tw:size-5 tw:ltr:-scale-x-100" />
            </Link>
            <h1 className="tw:m-0 tw:flex tw:items-center tw:gap-2 tw:text-xl tw:font-semibold">
              <Archive className="tw:size-5 tw:text-muted-foreground" />
              {t("Mandoob:Chats.FilterArchived")}
            </h1>
          </div>
        ) : (
          <h1 className="tw:m-0 tw:mb-3 tw:text-xl tw:font-semibold">
            {t("Mandoob:Chats.Title")}
          </h1>
        )}
        <div className="tw:relative tw:md:max-w-md">
          <Search className="tw:pointer-events-none tw:absolute tw:start-3 tw:top-1/2 tw:size-4 tw:-translate-y-1/2 tw:text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("Mandoob:Chats.SearchPlaceholder")}
            className="tw:ps-9"
          />
        </div>
        {archived ? null : (
          <div className="tw:mt-2.5 tw:flex tw:gap-1.5 tw:overflow-x-auto tw:pb-0.5 tw:[scrollbar-width:none]">
            {STATUS_FILTERS.map((status) => {
              const selected = statusFilter === status;
              const unreadHere =
                status === null ? 0 : (chipUnread[status] ?? 0);
              return (
                <button
                  key={status ?? "all"}
                  type="button"
                  onClick={() => setStatusFilter(status)}
                  aria-pressed={selected}
                  className={cn(
                    "tw:flex tw:shrink-0 tw:items-center tw:gap-1.5 tw:whitespace-nowrap tw:rounded-full tw:border tw:border-transparent tw:px-3 tw:py-1 tw:text-xs tw:font-medium tw:transition-colors",
                    selected
                      ? "tw:bg-brand tw:text-brand-foreground"
                      : "tw:bg-muted tw:text-muted-foreground tw:hover:text-foreground",
                  )}
                >
                  {status === null
                    ? t("Mandoob:Chats.FilterAll")
                    : status === "Undelivered"
                      ? t("Mandoob:Chats.FilterUndelivered")
                      : t(DELIVERY_STATUS_KEYS[status])}
                  {/* Unread badge: a reply waiting behind this chip. */}
                  {unreadHere > 0 ? (
                    <span
                      className={cn(
                        "tw:flex tw:h-4 tw:min-w-4 tw:items-center tw:justify-center tw:rounded-full tw:px-1 tw:text-[10px] tw:font-semibold",
                        selected
                          ? "tw:bg-brand-foreground tw:text-brand"
                          : "tw:bg-brand tw:text-brand-foreground",
                      )}
                    >
                      <Num>{unreadHere}</Num>
                    </span>
                  ) : null}
                </button>
              );
            })}
            {/* Archive entry at the row's end: chats closed for 24h+ live on
              their own page, out of the way of the day-to-day views. */}
            <Link
              to="/chats"
              search={{ archived: true }}
              aria-label={t("Mandoob:Chats.FilterArchived")}
              title={t("Mandoob:Chats.FilterArchived")}
              className="tw:ms-auto tw:flex tw:size-6.5 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-transparent tw:bg-muted tw:text-muted-foreground tw:transition-colors tw:hover:text-foreground"
            >
              <Archive className="tw:size-3.5" strokeWidth={2} />
            </Link>
          </div>
        )}
      </header>

      <div
        className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto"
        onScroll={onScroll}
      >
        {isLoading ? (
          <div>
            {Array.from({ length: 8 }).map((_, i) => (
              <RowSkeleton key={i} />
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-3 tw:px-8 tw:py-16 tw:text-center">
            <div className="tw:flex tw:size-16 tw:items-center tw:justify-center tw:rounded-full tw:bg-brand-50">
              <PackagePlus
                className="tw:size-7 tw:text-brand"
                strokeWidth={1.5}
              />
            </div>
            <p className="tw:m-0 tw:text-lg tw:font-semibold">
              {filtering
                ? t("Mandoob:Chats.NoResults")
                : t("Mandoob:Chats.EmptyTitle")}
            </p>
            {!filtering && (
              <>
                <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
                  {t("Mandoob:Chats.EmptySubtitle")}
                </p>
                <Button asChild className="tw:mt-2">
                  <Link to="/chats/new">
                    <PackagePlus className="tw:size-5" />
                    {t("Mandoob:NewDelivery.Title")}
                  </Link>
                </Button>
              </>
            )}
          </div>
        ) : (
          <>
            {conversations.map((conversation) => (
              <ConversationRow
                key={conversation.id}
                conversation={conversation}
              />
            ))}
            {isFetchingNextPage && <RowSkeleton />}
          </>
        )}
      </div>

      {archived ? null : (
        <Link
          to="/chats/new"
          aria-label={t("Mandoob:NewDelivery.Title")}
          // Fixed above the bottom nav on phones; absolute inside the page (or
          // the desktop conversations aside) from md up.
          // Bigger on phones (primary action, thumb-reach); regular inside the
          // desktop aside.
          className="tw:fixed tw:bottom-[calc(84px+env(safe-area-inset-bottom))] tw:end-4 tw:md:absolute tw:md:bottom-6 tw:z-40 tw:flex tw:size-[4.5rem] tw:items-center tw:justify-center tw:rounded-full tw:bg-brand tw:text-brand-foreground tw:shadow-lg tw:transition-transform tw:active:scale-95 tw:md:size-14"
        >
          <PackagePlus className="tw:size-8 tw:md:size-6" strokeWidth={1.75} />
        </Link>
      )}
    </div>
  );
}
